import {
  Autocomplete,
  Box,
  FormLabel,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { t } from 'i18next';
import SearchIcon from '@mui/icons-material/Search';

interface IOption {
  id: string;
  name: string;
  inactive?: boolean;
}

interface ISearchFieldProps {
  name?: string;
  label?: string;
  disabled?: boolean;
  optional?: boolean;
  placeholder?: string;
  search?: string;
  value: string;
  error?: boolean;
  options: IOption[];
  onSearchChange: (newSearchQuery: string) => void;
  onValueChange: (optionId: string) => void;
  noOptionsText?: string;
}

export const SearchField = ({
  name,
  disabled = false,
  label,
  optional = false,
  value,
  search = '',
  options,
  onSearchChange,
  onValueChange,
  placeholder = 'Search',
  error = false,
  noOptionsText = t('common.autocompletePlaceholder.noOptions'),
  // ...props
}: ISearchFieldProps) => {
  const selectedOption =
    value && options.some(option => option.id === value)
      ? options.find(option => option.id === value)
      : null;

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {label && (
        <FormLabel
          htmlFor={name}
          sx={{ width: '100%', pb: '0.75rem' }}
          required={!optional}
        >
          {label}
        </FormLabel>
      )}
      <Autocomplete
        // {...props}
        options={options}
        getOptionLabel={option => option.name || ''}
        filterOptions={x => x}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={selectedOption}
        inputValue={search?.length > 0 ? search : selectedOption?.name || ''}
        onInputChange={(event, newInputValue) => {
          if (event) {
            onSearchChange(newInputValue);
          }
        }}
        onChange={(event, newValue) => {
          if (disabled) return;
          onValueChange(newValue?.id ?? '');
        }}
        renderOption={(liProps, option) => (
          <li {...liProps} key={option.id}>
            <Typography
              sx={{
                color: option?.inactive ? 'text.disabled' : 'text.primary',
              }}
            >
              {option.name}
            </Typography>
          </li>
        )}
        noOptionsText={noOptionsText}
        renderInput={params => (
          <TextField
            {...params}
            required={!optional}
            label={placeholder}
            fullWidth
            error={error}
            slotProps={{
              ...params.slotProps,
              inputLabel: {
                sx: {
                  '&.Mui': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                  },
                  '&.MuiInputLabel-root': {
                    transform: `translate(36px, 1rem)`,
                    pl: '0.5rem',
                  },
                  '&.MuiFormLabel-filled, &.Mui-focused': {
                    transform: 'translate(14px, -9px) scale(0.75)',
                    pl: 0,
                  },
                  '&.MuiInputLabel-root:not(.Mui-focused):not(.MuiFormLabel-filled) ~ .MuiInputBase-root .MuiOutlinedInput-notchedOutline legend':
                    {
                      maxWidth: 0,
                    },
                },
              },
              input: {
                ...params.slotProps.input,
                sx: {
                  color:
                    selectedOption?.inactive || search?.includes('Inactive')
                      ? 'gray'
                      : 'black',
                },
                startAdornment: (
                  <>
                    <InputAdornment position='start'>
                      <SearchIcon />
                    </InputAdornment>
                    {params.slotProps.input?.startAdornment}
                  </>
                ),
                endAdornment: (
                  <Box
                    sx={{
                      '.MuiAutocomplete-endAdornment': {
                        display: 'flex',
                        alignItems: 'center',
                        flexDirection: 'row',
                      },
                    }}
                  >
                    {params.slotProps.input?.endAdornment}
                  </Box>
                ),
              },
            }}
          />
        )}
        autoComplete
        includeInputInList
        disabled={disabled}
        // size='small'
      />
    </Box>
  );
};
